import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import RichTextDisplay from 'Components/UI/RichTextDisplay';
import { validateType } from 'Helpers/Validation';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import LoadingIndicator from 'Components/UI/LoadingIndicator';
import { Typography } from '@material-ui/core';
import useCollection from 'Hooks/useCollection';

const styles = theme => ({
  root: {},
  actionItemTitle: {
    color: theme.palette.facet.accentGreen,
  },
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  richTextContent: {
    flex: 1,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  actionItems: {
    flex: 1,
  },
});

const fetchHouseholdMeetings = async (household, data) => {
  try {
    const agendaData = JSON.parse(data);
    return await household.relationship('meetings')?.link('related')?.Get({
      params: {
        id: agendaData.meetingId.toString(),
      },
    });
  } catch (e) {
    displayErrorMessage(e, 'Could not fetch related meetings');
    return null;
  }
};

const getMeetingAgendaDataFromMeetings = (householdMeetings, id) => {
  const meetingAgenda = householdMeetings
    ?.resources()
    .head()
    ?.attr('agendas')
    ?.resources()
    .list()
    .find(v => v.id().toString() === id);
  if (!meetingAgenda) {
    return null;
  }
  return {
    data: meetingAgenda.export(),
  };
};

const AgendaDetail = ({
  classes, className, householdResource, eventData, eventId,
}) => {
  const [loading, setLoading] = useState(false);
  const [extraContent, setExtraContent] = useState(null);
  const { collection, setCollection } = useCollection(null);

  const fetchData = async () => {
    try {
      const householdMeetings = await fetchHouseholdMeetings(householdResource, eventData, eventId);
      const { data: { attributes, meta } } = getMeetingAgendaDataFromMeetings(householdMeetings, eventId);
      const { att } = validateType(attributes, meta);

      setExtraContent(att.content);

      const actionItems = await householdMeetings?.resources()?.head().relationship('simpleActionItemLinks').link('related')
        .Get();
      setCollection(actionItems);
    } catch (e) {
      displayErrorMessage(e, 'Could not initialize Agenda detail');
    }
  };

  useEffect(() => {
    setLoading(true);

    fetchData().finally(() => setLoading(false));
  }, [eventId, householdResource]);

  return (
    <div className={classNames(classes.root, className)}>
      {loading
        ? <LoadingIndicator />
        : (
          <div className={classes.contentContainer}>
            <div className={classes.richTextContent}>
              <RichTextDisplay content={extraContent} />
            </div>

            <div className={classes.actionItems}>
              <Typography
                variant="h3"
                className={classes.actionItemTitle}
              >
                Action Items
              </Typography>
              <ul className={classes.actionItemList}>
                {collection?.resources().list().map(item => (
                  <li>
                    {item.attr('name')}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )
      }
    </div>
  );
};

AgendaDetail.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  householdResource: PropTypes.object.isRequired,
  eventData: PropTypes.string.isRequired,
  eventId: PropTypes.string.isRequired,
};

AgendaDetail.defaultProps = {
  className: '',
};

export default withStyles(styles)(AgendaDetail);
